import { Apis } from '@/appSettings'
import axios from 'axios'
import type { AvailableAddons, GroupItem, OfferPackage, OfferProperty, OfferTravel, PackageItemModel, SaveOfferPackageAddonsRequest, UpdateOfferPackageDto } from 'BrokerTsLib/src/dtos'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import { longStaleTime } from 'BrokerTsLib/src/hooks'
import { useMutation, useQueries, useQuery } from 'react-query'
import { useUrl } from '.'

type OfferPackageType = OfferPackage | OfferProperty | OfferTravel

const fetchOfferAvailablePackageItems = async (url: string, offerId: number) => {
  return axios.get<PackageItemModel[]>(`${url}/GetOfferAvailablePackageItems?offerId=${offerId}`)
}

export const useGetOffersAvailablePackageItems = (offersIds: number[]) => {
  const url = useUrl(Apis.offer)!

  return useQueries(
    offersIds.map((id) => ({
      queryKey: ['GetOfferAvailablePackageItems', id],
      queryFn: () => fetchOfferAvailablePackageItems(url, id),
      staleTime: longStaleTime,
      enabled: offersIds.length > 0,
      refetchOnWindowFocus: false,
    }))
  )
}

export const useGetAvailablePackages = (offerId: number, sectionId?: number, options?: GenericQueryOptions<GroupItem[]>) => {
  const url = useUrl(Apis.offer)

  return useQuery(['GetAvailablePackages', offerId, sectionId]
    , () => axios.get<GroupItem[]>(`${url}/GetAvailablePackages?offerId=${offerId}&groupId=${sectionId}`)
    , {
      staleTime: longStaleTime,
      refetchOnWindowFocus: false,
      ...options
    })
}

export const useGetGrouppedAvailablePackages = (offerId: number, sectionId?: number, options?: GenericQueryOptions<GroupItem[]>) => {
  const { data } = useGetAvailablePackages(offerId, sectionId, options)

  return data?.data.reduce((pv, cv) => {
    return { ...pv, [cv.groupId ?? '']: [...(pv[cv.groupId ?? ''] ?? []), cv] }
  }, {} as { [n: string]: GroupItem[] }) ?? {}
}

export const useUpdateOfferPackage = () => {
  const url = useUrl(Apis.offer)

  return useMutation({
    mutationFn: (data: UpdateOfferPackageDto) => axios.post(`${url}/UpdateOfferPackage`, data)
  })
}


export const useGetOfferAvailablePackageItems = (offerId: number, options?: GenericQueryOptions<AvailableAddons[] | PackageItemModel[]>) => {
  const url = useUrl(Apis.offer)!
  return useQuery(['GetOfferAvailablePackageItems', offerId]
    , () => fetchOfferAvailablePackageItems(url, offerId)
    , {
      staleTime: longStaleTime,
      refetchOnWindowFocus: false,
      ...options
    })
}

export const useSaveOfferParts = () => {
  const url = useUrl(Apis.offer)!
  return useMutation({ mutationFn: (offer: OfferPackageType) => axios.post(`${url}/SaveOfferParts`, offer) })
}

export const useSaveOfferAddons = () => {
  const url = useUrl(Apis.offer)!
  return useMutation({
    mutationFn: (offer: SaveOfferPackageAddonsRequest) =>
      axios.post(`${url}/SaveOfferAddons`, offer)
  })
}

export const useResolveChangedParts = () => {
  const url = useUrl(Apis.offer)!

  return useMutation({
    mutationFn: (data: { offer: OfferPackage; packageItemsIds: number[] }) =>
      axios.post<OfferPackage>(`${url}/ResolveChangedParts`, data),
  })
}


export const useResolveChangedPart = () => {
  const url = useUrl(Apis.offer)

  return useMutation({
    mutationFn: (data: {
      offer: OfferPackage,
      packageItemId: number
    }) => axios.post<OfferPackage>(`${url}/ResolveChangedPart`, data)
  })
}

export const useGetOfferAvailableAddons = (offerId: number, options?: GenericQueryOptions<PackageItemModel[]>) => {
  const url = useUrl(Apis.offer)

  return useQuery(['getOfferAvailableAddons', offerId]
    , () => axios.get<PackageItemModel[]>(`${url}/GetOfferAvailableAddons?offerId=${offerId}`)
    , options)
}